/**
 * MODULE: domain/pages/frontend/utils/pageShareMemberUtils.ts
 * PURPOSE: Helpers for page share modal member picker and grant list labels
 * RECENT: 2026-05-24 [2.2.1186] — normalized user id lookup; short display names without uuid fallback
 */

import type { WorkspaceMember } from '@domain/workspace';

export type WorkspaceMemberMap = Map<string, WorkspaceMember>;

export type MemberDisplayFallback = {
  id: string;
  name?: string | null;
  email?: string | null;
};

export function normalizeUserIdKey(userId: string): string {
  return userId.trim().toLowerCase();
}

export function userIdsMatch(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  if (!a || !b) {
    return false;
  }
  return normalizeUserIdKey(a) === normalizeUserIdKey(b);
}

export function formatWorkspaceMemberLabel(member: WorkspaceMember): string {
  const name = member.user?.name?.trim();
  const email = member.user?.email?.trim();
  if (name && email) {
    return `${name} (${email})`;
  }
  if (name) {
    return name;
  }
  if (email) {
    return email;
  }
  return 'Unknown user';
}

function resolveNameFromMember(member: WorkspaceMember | undefined): string | null {
  const name = member?.user?.name?.trim();
  if (name) {
    return name;
  }
  const email = member?.user?.email?.trim();
  if (email) {
    const local = email.split('@')[0]?.trim();
    if (local) {
      return local;
    }
  }
  return null;
}

function resolveNameFromFallback(fallback: MemberDisplayFallback | undefined): string | null {
  if (!fallback) {
    return null;
  }
  const name = fallback.name?.trim();
  if (name) {
    return name;
  }
  const email = fallback.email?.trim();
  if (email) {
    const local = email.split('@')[0]?.trim();
    if (local) {
      return local;
    }
  }
  return null;
}

export function getGrantableWorkspaceMembers(
  members: WorkspaceMember[],
  options: { pageOwnerId: string; grantedUserIds: string[] }
): WorkspaceMember[] {
  const grantedIds = new Set(options.grantedUserIds.map(normalizeUserIdKey));
  const ownerKey = normalizeUserIdKey(options.pageOwnerId);
  return members.filter(
    (member) =>
      normalizeUserIdKey(member.user_id) !== ownerKey &&
      !grantedIds.has(normalizeUserIdKey(member.user_id))
  );
}

export function buildWorkspaceMemberMap(members: WorkspaceMember[]): WorkspaceMemberMap {
  const map = new Map<string, WorkspaceMember>();
  for (const member of members) {
    map.set(normalizeUserIdKey(member.user_id), member);
    if (member.user?.id) {
      map.set(normalizeUserIdKey(member.user.id), member);
    }
  }
  return map;
}

export function lookupWorkspaceMember(
  membersByUserId: WorkspaceMemberMap,
  userId: string
): WorkspaceMember | undefined {
  return membersByUserId.get(normalizeUserIdKey(userId));
}

/** Short human label for metadata footers and activity rows (name only, never raw user id). */
export function resolveMemberShortDisplayName(
  membersByUserId: WorkspaceMemberMap,
  userId: string,
  fallback?: MemberDisplayFallback
): string {
  const fromMember = resolveNameFromMember(lookupWorkspaceMember(membersByUserId, userId));
  if (fromMember) {
    return fromMember;
  }
  if (fallback && userIdsMatch(fallback.id, userId)) {
    const fromFallback = resolveNameFromFallback(fallback);
    if (fromFallback) {
      return fromFallback;
    }
  }
  return 'Unknown user';
}

export function resolveMemberDisplayLabel(
  membersByUserId: Map<string, WorkspaceMember>,
  userId: string,
  fallback?: MemberDisplayFallback
): string {
  const member = lookupWorkspaceMember(membersByUserId, userId);
  if (member) {
    return formatWorkspaceMemberLabel(member);
  }
  if (fallback && userIdsMatch(fallback.id, userId)) {
    const name = fallback.name?.trim();
    const email = fallback.email?.trim();
    if (name && email) {
      return `${name} (${email})`;
    }
    if (name) {
      return name;
    }
    if (email) {
      return email;
    }
  }
  return 'Unknown user';
}
